import { supabase } from "@/lib/supabase";
import { parsePrSearch, rankPrResults } from "@/lib/releaseSearch";
import { enrichReleaseTasks } from "./releaseTaskLinks";
import type { ReleasePr } from "./releases";
import { mergeSearchPrs } from "@/lib/releaseSearchResults";

type Reference = Pick<
  ReleasePr,
  "repo" | "number" | "url" | "head_sha" | "state" | "draft" | "pr_body"
>;
type Canonical = {
  repo: string;
  number: number;
  title: string;
  head_sha: string;
  state: string;
  gh_updated_at: string | null;
};
export function historyPr(row: Canonical): ReleasePr {
  return {
    ...row,
    url: `https://github.com/${row.repo}/pull/${row.number}`,
    identity_only: true,
    author: "",
    draft: false,
    merge_state: "UNKNOWN",
    level: null,
    labels: [],
    release_manual: false,
    approved_sha: null,
    approved_at: null,
    approved_mode: null,
    last_outcome: null,
    last_reason: null,
  };
}

// Preview-compatible history read: existing JumpStart SELECT RLS, never Admin
// tables or a new permission path. Metadata is not readiness/approval evidence.
export async function searchPrIdentity(
  query: string,
  source: ReleasePr[],
  repos: string[],
  readReference: (repo: string, number: number) => Promise<Reference>,
) {
  const identity = parsePrSearch(query);
  if (!identity || identity.number > 2147483647) {
    return { prs: rankPrResults(query, source), warning: "" };
  }
  let warning = "", canonical: Canonical[] = [];
  const allowed = repos.filter((repo) => /^the-Otomator\/[\w.-]+$/.test(repo));
  try {
    let read = supabase.from("task_pull_requests").select(
      "repo,number,title,head_sha,state,gh_updated_at",
    ).eq("number", identity.number).order("repo");
    if (identity.repo) read = read.eq("repo", `the-Otomator/${identity.repo}`);
    if (allowed.length) read = read.in("repo", allowed);
    const result = await read;
    if (result.error) throw result.error;
    canonical = result.data;
  } catch {
    warning = "חיפוש ההיסטוריה חלקי — לא ניתן לקרוא את קישורי המשימות כרגע.";
  }
  const rows = new Map(
    mergeSearchPrs(rankPrResults(query, source), canonical.map(historyPr)).map(
      (pr) => [`${pr.repo}#${pr.number}`, pr],
    ),
  );
  const candidates = identity.repo
    ? allowed.filter((repo) => repo === `the-Otomator/${identity.repo}`)
    : [
      ...new Set(
        [...rows.values()].filter((pr) =>
          pr.number === identity.number && allowed.includes(pr.repo)
        ).map((pr) => pr.repo),
      ),
    ];
  // Long bare numeric strings are also migration version prefixes. Avoid ten
  // GitHub probes when the canonical store has no PR with that identity.
  const targets = candidates.length
    ? candidates
    : !identity.repo && String(identity.number).length < 8
    ? allowed
    : [];
  const references = await Promise.allSettled(
    targets.map((repo) => readReference(repo, identity.number)),
  );
  references.forEach((result, index) => {
    if (result.status === "rejected") {
      const key = `${targets[index]}#${identity.number}`, old = rows.get(key);
      if (old) {
        rows.set(key, {
          ...old,
          identity_only: true,
          workflow: null,
          approved_sha: null,
          approved_at: null,
          approved_mode: null,
        });
      }
      if (old || !String(result.reason?.code ?? "").endsWith("404")) {
        warning =
          "לא ניתן לאמת חלק מהתוצאות מול GitHub; ראיות חסרות נשארות לא מאומתות.";
      }
      return;
    }
    const ref = result.value,
      key = `${ref.repo}#${ref.number}`,
      old = rows.get(key);
    if (
      ref.repo !== targets[index] || ref.number !== identity.number ||
      !["open", "closed", "merged"].includes(ref.state) ||
      !/^[a-f0-9]{40}$/i.test(ref.head_sha)
    ) {
      warning = "זהות תוצאת GitHub לא אומתה; אין להסתמך עליה לפעולה.";
      const expectedKey = `${targets[index]}#${identity.number}`,
        previous = rows.get(expectedKey);
      if (previous) {
        rows.set(expectedKey, {
          ...previous,
          identity_only: true,
          workflow: null,
          approved_sha: null,
          approved_at: null,
          approved_mode: null,
        });
      }
      return;
    }
    const same = old?.head_sha === ref.head_sha && old.state === ref.state;
    rows.set(key, {
      ...(same ? old : historyPr({
        repo: ref.repo,
        number: ref.number,
        title: old?.title ?? `PR #${ref.number}`,
        head_sha: ref.head_sha,
        state: ref.state,
        gh_updated_at: null,
      })),
      ...ref,
    });
  });
  return {
    prs: rankPrResults(query, await enrichReleaseTasks([...rows.values()])),
    warning,
  };
}
